/**
 * Назначение: платформенная сводка: выручка, организации, персонал.
 * Кто: СА и персонал платформы (platform:dashboard:view).
 * Вход: Сайдбар: Платформа → Сводка.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { fetchSuperadminPlatformDashboard } from '../../features/superadmin/api/platform-dashboard.api';
import type { SuperadminPlatformDashboardDto } from '../../features/superadmin/api/platform-dashboard.types';
import { fetchDashboardExtended, type DashboardExtendedDto } from '../../features/superadmin/api/platform-dashboard-extended.api';
import { ROUTE_PATHS } from '../../app/router/route-paths';
import type { CapabilitiesSnapshot } from '../../entities/me/me-contract.types';
import {
  canAccessPlatformDashboard,
  canAccessPlatformOrganizationsList,
  canAccessPlatformPlansList,
  canAccessPlatformUsersPage,
  canAccessSuperadminClientsCatalogPage,
  hasPermission,
  isRouteAllowed
} from '../../shared/access/access';
import { toBackendStaffSpaPath } from '../../shared/access/route-bridge';
import type { StaffSPARoutePath } from '../../shared/access/staff-spa-route-paths';
import { useAuth } from '../../shared/auth/use-auth';
import { userFacingLoadError } from '../../shared/lib/staff-user-facing-error';
import { Alert } from '../../shared/ui/Alert';
import { Button, getButtonClassNames } from '../../shared/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../shared/ui/Card';
import { Container } from '../../shared/ui/Container';
import { EmptyState } from '../../shared/ui/EmptyState';
import { FieldHelpIcon } from '../../shared/ui/FieldHelpIcon';
import PaymentStatsBlock from './PaymentStatsBlock';
import PaymentTableBlock from './PaymentTableBlock';
import { Spinner } from '../../shared/ui/Spinner';
import { Stack } from '../../shared/ui/Stack';
import { useStaffToast } from '../../shared/ui/StaffToast';
import {
  WeeklyGrowthAreaChart,
  AuditDailyBarChart
} from '../../shared/ui/PlatformCharts/PlatformCharts';

function canFollowActionRoute(
  capabilities: CapabilitiesSnapshot | null | undefined,
  actionRoute: string | null
): actionRoute is string {
  if (!actionRoute) return false;
  const key = toBackendStaffSpaPath(actionRoute) as StaffSPARoutePath | null;
  if (!key) return false;
  return isRouteAllowed(capabilities, key);
}

function alertVariant(t: SuperadminPlatformDashboardDto['alerts'][number]['type']): 'info' | 'warning' | 'error' {
  if (t === 'danger') return 'error';
  if (t === 'warning') return 'warning';
  return 'info';
}

export default function SuperadminDashboardPage() {
  const { me, apiClient } = useAuth();
  const toast = useStaffToast();
  const cap = me?.capabilities;
  const allowed = canAccessPlatformDashboard(cap);

  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [data, setData] = useState<SuperadminPlatformDashboardDto | null>(null);

  const [chartsLoading, setChartsLoading] = useState(false);
  const [chartsError, setChartsError] = useState<string | null>(null);
  const [extended, setExtended] = useState<DashboardExtendedDto | null>(null);

  const reload = useCallback(async () => {
    if (!allowed) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadFailed(false);
    try {
      const row = await fetchSuperadminPlatformDashboard(apiClient);
      setData(row);
    } catch (e) {
      setData(null);
      setLoadFailed(true);
      toast({ variant: 'error', message: userFacingLoadError(e) });
    } finally {
      setLoading(false);
    }
  }, [allowed, apiClient, toast]);

  const reloadCharts = useCallback(async () => {
    if (!allowed) return;
    setChartsLoading(true);
    setChartsError(null);
    try {
      const ext = await fetchDashboardExtended(apiClient);
      ext.orgCount = data?.organizations.total ?? 0;
      ext.userTotal = data?.platform_users.total ?? 0;
      setExtended(ext);
    } catch (e) {
      setExtended(null);
      setChartsError(userFacingLoadError(e));
    } finally {
      setChartsLoading(false);
    }
  }, [allowed, apiClient, data?.organizations.total, data?.platform_users.total]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (data && !extended && !chartsLoading) {
      void reloadCharts();
    }
  }, [data, extended, chartsLoading, reloadCharts]);

  const gwSummary = useMemo(() => {
    const g = extended?.gw;
    if (!g) return null;
    const parts: string[] = [];
    if (g.robokassa_enabled) parts.push('Robokassa');
    if (g.yookassa_enabled) parts.push('ЮKassa');
    if (g.tochka_enabled) parts.push('Точка');
    const active = g.active_payment_gateway || '—';
    return `${active}${parts.length ? ` · вкл: ${parts.join(', ')}` : ''}`;
  }, [extended?.gw]);

  if (!allowed) {
    return <Navigate to={ROUTE_PATHS.forbidden} replace />;
  }

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Spinner label="Загрузка сводки платформы…" />
      </Container>
    );
  }

  if (loadFailed || !data) {
    return (
      <Container maxWidth="lg">
        <EmptyState title="Не удалось загрузить сводку" description="Попробуйте ещё раз. Если ошибка повторяется, обратитесь к администратору платформы.">
          <Button type="button" variant="secondary" className="mt-3" onClick={() => void reload()}>
            Повторить
          </Button>
        </EmptyState>
      </Container>
    );
  }

  const canCreateOrg =
    hasPermission(cap, 'platform:organizations:create') && canAccessPlatformOrganizationsList(cap);
  const canPlans = canAccessPlatformPlansList(cap);
  const canPlatformUsers = canAccessPlatformUsersPage(cap);
  const canClientsCatalog = canAccessSuperadminClientsCatalogPage(cap);

  const hasProblems = extended && (extended.paymentFailures > 0 || extended.paymentPending > 0 || extended.disabledModules > 0);

  return (
    <Container maxWidth="lg">
      <Stack gap={4}>
        {/* Основная сводка */}
        <Card>
          <CardHeader>
            <CardTitle>Платформа</CardTitle>
            <CardDescription>
              Ключевые показатели, динамика оплат и роста по аудиту платформы.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <MetricTile label="Организации, всего" value={data.organizations.total} />
              <MetricTile label="Активные организации" value={data.organizations.active} />
              <MetricTile
                label="Без тарифа"
                value={data.organizations.without_plan}
                hint="Организации без подтверждённой даты оплаченного периода."
              />
              <MetricTile
                label="Истекают скоро"
                value={data.organizations.near_expiration}
                hint="Оплаченный период заканчивается в ближайшие 14 дней."
              />
              <MetricTile
                label="Тарифные планы"
                value={data.plans.total}
                hint={`Активных планов в каталоге: ${data.plans.active}.`}
              />
              <MetricTile label="Пользователи платформы" value={data.platform_users.total} hint={`Активных записей: ${data.platform_users.active}.`} />
            </div>
          </CardContent>
        </Card>

        {/* Проблемы и риски */}
        {extended && hasProblems ? (
          <Card data-testid="platform-problems">
            <CardHeader>
              <CardTitle className="text-base">Проблемы и риски</CardTitle>
              <CardDescription>
                Отклонения, требующие внимания: ошибки оплат, отключённые модули, зависшие платежи.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 sm:grid-cols-3">
              {extended.paymentFailures > 0 ? (
                <Link to={`${ROUTE_PATHS.auditPayments}?search=error`} className="block hover:opacity-90">
                  <Alert variant="error" title={`Ошибки оплат: ${extended.paymentFailures}`}>
                    <p className="staff-m-0 text-sm cursor-pointer underline decoration-dotted">
                      Платёжных транзакций со статусом ошибки за последние 30 дней. Нажмите для подробностей.
                    </p>
                  </Alert>
                </Link>
              ) : null}
              {extended.paymentPending > 0 ? (
                <Alert variant="warning" title={`Зависшие платежи: ${extended.paymentPending}`}>
                  <p className="staff-m-0 text-sm">Платежи в статусе pending дольше ожидаемого.</p>
                </Alert>
              ) : null}
              {extended.disabledModules > 0 ? (
                <Alert variant="warning" title={`Отключённые модули: ${extended.disabledModules}`}>
                  <p className="staff-m-0 text-sm">Модули платформы, выключенные глобально. Проверьте в настройках.</p>
                </Alert>
              ) : null}
            </CardContent>
          </Card>
        ) : null}

        {/* Шлюзы и MRR */}
        {extended ? (
          <Card>
            <CardHeader>
              <CardDescription>Глобальные шлюзы</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm font-medium leading-snug">{gwSummary ?? '—'}</p>
            </CardContent>
          </Card>
        ) : null}

        {/* Статистика платежей (из payments table) */}
        <PaymentStatsBlock apiClient={apiClient} />
        <PaymentTableBlock apiClient={apiClient} />

        {/* Рост организаций и пользователей */}
        {extended && (extended.orgWeeklyGrowth.length > 0 || extended.userWeeklyGrowth.length > 0) ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Новые организации</CardTitle>
                <CardDescription>По неделям: сколько организаций появилось на платформе.</CardDescription>
              </CardHeader>
              <CardContent>
                <WeeklyGrowthAreaChart data={extended.orgWeeklyGrowth} label="Организации" />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Новые пользователи</CardTitle>
                <CardDescription>По неделям: сколько пользователей зарегистрировалось.</CardDescription>
              </CardHeader>
              <CardContent>
                <WeeklyGrowthAreaChart data={extended.userWeeklyGrowth} label="Пользователи" />
              </CardContent>
            </Card>
          </div>
        ) : null}

        {/* Аудит по дням */}
        {extended && extended.auditDaily.length > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">События аудита (14 дней)</CardTitle>
              <CardDescription>Все события аудита платформы за последние 14 дней.</CardDescription>
            </CardHeader>
            <CardContent>
              <AuditDailyBarChart data={extended.auditDaily} />
            </CardContent>
          </Card>
        ) : null}

        {/* Загрузка графиков */}
        {chartsLoading ? (
          <Card>
            <CardContent className="py-6">
              <Spinner label="Загрузка графиков…" />
            </CardContent>
          </Card>
        ) : null}

        {/* Ошибка графиков */}
        {chartsError ? (
          <Card>
            <CardContent className="py-4">
              <Alert variant="error" title="Не удалось загрузить графики">
                {chartsError}
              </Alert>
            </CardContent>
          </Card>
        ) : null}

        {/* Сигналы */}
        {data.alerts.length > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Сигналы</CardTitle>
              <CardDescription>Рекомендуемые действия по данным платформы.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {data.alerts.map((a, i) => (
                <Alert key={`${a.title}-${i}`} variant={alertVariant(a.type)} title={a.title}>
                  <p className="text-sm">{a.description}</p>
                  {canFollowActionRoute(cap, a.action_route) ? (
                    <p className="mt-2">
                      <Link className={getButtonClassNames({ variant: 'secondary', size: 'sm' })} to={a.action_route}>
                        Перейти
                      </Link>
                    </p>
                  ) : null}
                </Alert>
              ))}
            </CardContent>
          </Card>
        ) : null}

        {/* Быстрые действия */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Быстрые действия</CardTitle>
            <CardDescription>Ссылки зависят от прав и маршрутов в текущей сессии.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            {canCreateOrg ? (
              <Link
                className={getButtonClassNames({ variant: 'secondary', size: 'sm' })}
                to={ROUTE_PATHS.superadminOrganizations}
              >
                Создать организацию
              </Link>
            ) : null}
            {canPlans ? (
              <Link className={getButtonClassNames({ variant: 'secondary', size: 'sm' })} to={ROUTE_PATHS.superadminPlans}>
                Тарифные планы
              </Link>
            ) : null}
            {canPlatformUsers ? (
              <Link
                className={getButtonClassNames({ variant: 'secondary', size: 'sm' })}
                to={ROUTE_PATHS.superadminPlatformUsers}
              >
                Пользователи платформы
              </Link>
            ) : null}
            {canClientsCatalog ? (
              <Link
                className={getButtonClassNames({ variant: 'secondary', size: 'sm' })}
                to={ROUTE_PATHS.superadminClientsCatalog}
              >
                Клиенты организаций
              </Link>
            ) : null}
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
}

function MetricTile({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <div className="rounded-sm border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-card))] p-4">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium text-[hsl(var(--ui-muted-foreground))]">{label}</p>
        {hint ? <FieldHelpIcon text={hint} /> : null}
      </div>
      <p className="mt-2 text-2xl font-semibold tabular-nums text-[hsl(var(--ui-foreground))]">{value}</p>
    </div>
  );
}
